import { ConversationCardTag } from '@televet/kibble-ui/build/patterns/ConversationCard/components/conversationCardTags.component';
import { ChannelTagType, TagEntity } from '@televet/shared-types';
import { useCallback, useMemo } from 'react';
import { useRemoveChannelTagMutation } from 'shared/types/graphql';
import { getTagVariantByType } from 'shared/utils/tags';

interface ChannelTagProps {
  name: string;
  tagType: ChannelTagType;
  colorBackground: string;
  colorText: string;
  tagEntities?: TagEntity[];
}

interface UseChannelTagsInput {
  channelId: string;
  tags: ChannelTagProps[];
}

interface UseChannelTagsOutput {
  tags: ConversationCardTag[];
}

const useChannelTags = ({ channelId, tags }: UseChannelTagsInput): UseChannelTagsOutput => {
  const [removeChannelTag] = useRemoveChannelTagMutation();

  const handleRemoveChannelTag = useCallback(
    (tagName, e) => {
      e.stopPropagation();
      const newTags = tags?.filter(({ name }: { name: string }) => name.toLowerCase() !== tagName.toLowerCase());
      removeChannelTag({
        variables: { channelId, tagName },
        optimisticResponse: {
          removeChannelTag: {
            id: channelId,
            tags: newTags,
          },
        },
      });
    },
    [tags, removeChannelTag, channelId],
  );

  const sortedTags: ChannelTagProps[] = useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags]);

  const tagsProps = useMemo(() => {
    return (
      sortedTags.map((tag) => {
        const tagEntitiesLabels = tag.tagEntities?.map((t) => t.label) || [];
        const joinedLabels =
          tagEntitiesLabels.length > 3
            ? `${tagEntitiesLabels.slice(0, 3).join(', ')}, + ${tagEntitiesLabels.length - 3} more`
            : tagEntitiesLabels.join(', ');
        return {
          label: tag.name,
          ...(tag.tagType === ChannelTagType.Custom && tag.colorBackground && tag.colorText
            ? { bg: tag.colorBackground, color: tag.colorText }
            : { variant: getTagVariantByType(tag.tagType) }),
          tooltipContent: joinedLabels,
          onClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleRemoveChannelTag(tag.name, e),
        };
      }) || []
    );
  }, [handleRemoveChannelTag, sortedTags]);

  return {
    tags: tagsProps,
  };
};

export default useChannelTags;
